import { useField } from "formik";
import { OwcSelectDropdown, OwcListItem, OwcTypography, OwcProgressSpinner } from "@one/react";
import { useEffect, useState } from "react";
import { ColumnType, ValueAttributeInputModelType, THEME } from "@digitallab/grid-common-components";
import { react18WorkaroundProps } from "../../../constants/react18WorkaroundProps";

export const FormSelect = ({ column }: { column: ColumnType }) => {
  const [field, meta, helpers] = useField(column.name ?? "column");
  const [options, setOptions] = useState<ValueAttributeInputModelType[] | string[]>([]);
  const [conditionalField] = useField(
    column?.conditionalRequired ? column.conditionalRequired.dependsOnName : "no_condition"
  );
  const isRequired =
    conditionalField.name !== "no_condition"
      ? column.conditionalRequired?.condition(conditionalField.value)
      : column.required;
  const isTouched = Boolean(meta.touched);

  const handleSelectChange = ({ detail }: { detail: number[] }) => {
    if (typeof options[0] === "string") helpers.setValue((options as string[])?.[detail[0]]);
    else helpers.setValue((options as ValueAttributeInputModelType[])?.[detail[0]]);
    helpers.setTouched(true);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      if (
        column?.options &&
        typeof column.options === "object" &&
        typeof (column.options as Promise<unknown>).then === "function"
      ) {
        const resolvedOptions = await column.options;
        if (isMounted) {
          setOptions(resolvedOptions as ValueAttributeInputModelType[]);
        }
      } else if (Array.isArray(column?.options)) {
        setOptions(column.options as ValueAttributeInputModelType[]);
      }
    };
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [column.options]);

  return options.length > 0 ? (
    <div style={{ minHeight: THEME["one-spacer-80"] }}>
      <OwcSelectDropdown
        {...react18WorkaroundProps}
        className={isTouched && meta.error ? "select-dropdown-has-error" : ""}
        style={{ backgroundColor: THEME["one-color-cobas-gray-100"], width: "100%" }}
        label={`${column.label}${isRequired ? "*" : ""}`}
        onSelectChange={handleSelectChange}
        {...column.fieldProps}
      >
        {options?.map((option) => {
          if (typeof option === "string") {
            return (
              <OwcListItem {...react18WorkaroundProps} key={option} value={option} selected={option === field.value}>
                {option}
              </OwcListItem>
            );
          } else
            return (
              <OwcListItem
                {...react18WorkaroundProps}
                key={option.key}
                value={option.key}
                selected={option.key === field.value?.key}
              >
                {option.value}
              </OwcListItem>
            );
        })}
      </OwcSelectDropdown>
      <OwcTypography
        {...react18WorkaroundProps}
        variant="caption"
        style={{
          color: column.disabled ? THEME["one-color-gray-400"] : THEME["one-color-gray-600"],
          paddingLeft: THEME["one-spacer-16"]
        }}
      >
        {column.assistiveText}
      </OwcTypography>
      <OwcTypography
        {...react18WorkaroundProps}
        variant="caption"
        style={{ color: THEME["one-color-interaction-default-negative-1"], paddingLeft: THEME["one-spacer-16"] }}
      >
        {isTouched && meta.error}
      </OwcTypography>
    </div>
  ) : (
    <div style={{ display: "flex", justifyContent: "center", width: "100%", height: THEME["one-spacer-48"] }}>
      <OwcProgressSpinner {...react18WorkaroundProps} diameter={40} />
    </div>
  );
};
